import { computed, nextTick, ref, Ref } from 'vue';
import { ComplexModal } from '@studyportals/modal';
import { CampaignImportEligibilityResponse, ImportEligibilityIneligibleReason, SearchCampaign } from '@studyportals/campaign-management-api-interface';
import * as Ladda from 'ladda';
import importCampaignConfigurationPresenter from '@/presenters/import-campaign-configuration-presenter';
import datePresenter from '@/presenters/date-presenter';
import rollbarOperator from '@/infrastructure/rollbar-operator';
import store from '@/store';

type TStep = 'search' | 'review';

// Reasons a user can act on directly get shown as-is; the rest indicate a data/state
// inconsistency the UI shouldn't normally allow into, so they're logged and shown generically.
const INELIGIBLE_REASON_MESSAGES: Record<ImportEligibilityIneligibleReason, string | null> = {
	source_and_destination_are_same_campaign: `You can't import a campaign's configuration into itself. Choose a different campaign.`,
	date_periods_overlap: 'This campaign\'s dates overlap with the destination campaign. Choose a campaign with non-overlapping dates.',
	source_campaign_has_no_linked_organisations: 'This campaign has no linked organisations to import.',
	source_campaign_has_no_attached_entities: 'This campaign has no attached entities to import.',
	entities_already_attached_to_overlapping_campaign:
		'Some of this campaign\'s entities are already attached to another campaign with overlapping dates.',
	source_campaign_has_ended: 'This campaign has already ended and can no longer be used as a source.',
	source_campaign_not_found: null,
	destination_campaign_not_found: null,
	source_campaign_not_listing_type: null,
	destination_campaign_not_listing_type: null,
	destination_campaign_has_ended: null
};

const GENERIC_INELIGIBLE_MESSAGE = 'This campaign can\'t be imported right now. Please try again or choose a different campaign.';

export default class ImportCampaignConfigurationModalCode {
	public modalContent: Ref<HTMLElement | null> = ref(null);
	public searchInput: Ref<HTMLInputElement | null> = ref(null);
	public importButton: Ref<HTMLButtonElement | null> = ref(null);
	public step: Ref<TStep> = ref('search');
	public searchTerm: Ref<string> = ref('');
	public searchResults: Ref<SearchCampaign[]> = ref([]);
	public showSpinner = ref<boolean>(false);
	public eligibility: Ref<CampaignImportEligibilityResponse | null> = ref(null);
	public isValidatingEligibility = ref<boolean>(false);
	public isImporting = ref<boolean>(false);

	private modal: Ref<ComplexModal | undefined> = ref(undefined);
	private selectedCampaign: Ref<SearchCampaign | null> = ref(null);

	public ineligibleMessage = computed((): string => {
		if (!this.eligibility.value || this.eligibility.value.isEligible) {
			return '';
		}

		const reason = this.eligibility.value.reason;
		return INELIGIBLE_REASON_MESSAGES[reason] ?? GENERIC_INELIGIBLE_MESSAGE;
	});

	public formattedSourceDateRange = computed((): string => {
		if (!this.eligibility.value || !this.eligibility.value.isEligible) {
			return '';
		}

		const details = this.eligibility.value.details;
		return this.formatDateRange(details.sourceCampaignStartDate, details.sourceCampaignEndDate);
	});

	public formattedDestinationDateRange = computed((): string => {
		if (!this.eligibility.value || !this.eligibility.value.isEligible) {
			return '';
		}

		const details = this.eligibility.value.details;
		return this.formatDateRange(details.destinationCampaignStartDate, details.destinationCampaignEndDate);
	});

	public mounted(): void {
		this.prepareModal();
	}

	public unmounted(): void {
		window.ModalManager.close(this.modal.value);
	}

	public closeModal(): void {
		store.mutations.setImportCampaignConfigurationModalActive(false);
	}

	public async search(): Promise<void> {
		if (!this.searchInput.value || this.showSpinner.value) {
			return;
		}

		this.searchTerm.value = this.searchInput.value.value;

		if (this.searchTerm.value.trim() === '') {
			this.searchResults.value = [];
			return;
		}

		this.showSpinner.value = true;
		this.searchResults.value = await importCampaignConfigurationPresenter.searchCampaigns(this.searchTerm.value);
		this.showSpinner.value = false;
	}

	public async selectCampaign(campaign: SearchCampaign): Promise<void> {
		this.selectedCampaign.value = campaign;
		this.step.value = 'review';
		this.isValidatingEligibility.value = true;
		this.eligibility.value = await importCampaignConfigurationPresenter.validateImportEligibility(campaign.id);
		this.isValidatingEligibility.value = false;
		this.logIneligibleReasonIfUnexpected();
	}

	public backToSearch(): void {
		this.step.value = 'search';
		this.selectedCampaign.value = null;
		this.eligibility.value = null;
	}

	public async confirmImport(): Promise<void> {
		if (!this.selectedCampaign.value || !this.eligibility.value?.isEligible) {
			return;
		}

		this.isImporting.value = true;
		void this.triggerLoadingAnimation();

		const wasSuccessful = await importCampaignConfigurationPresenter.importCampaignConfigurations(
			this.selectedCampaign.value.id,
			this.eligibility.value.details
		);

		if (!wasSuccessful) {
			this.isImporting.value = false;
			return;
		}

		this.closeModal();
	}

	private logIneligibleReasonIfUnexpected(): void {
		if (!this.eligibility.value || this.eligibility.value.isEligible) {
			return;
		}

		const reason = this.eligibility.value.reason;
		if (INELIGIBLE_REASON_MESSAGES[reason] !== null) {
			return;
		}

		rollbarOperator.triggerError(new Error(`ImportCampaignConfigurationModal: unexpected ineligibility reason: ${reason}`));
	}

	// CampaignImportDetails types these fields as Date, but (like SearchCampaign) the response is raw JSON -
	// they arrive as ISO strings at runtime, not real Date instances.
	private formatDateRange(startDate: Date, endDate: Date): string {
		const formattedStartDate = datePresenter.transformDateObjectToUTCDateString(new Date(startDate), false);
		const formattedEndDate = datePresenter.transformDateObjectToUTCDateString(new Date(endDate), false);
		return `${formattedStartDate} - ${formattedEndDate}`;
	}

	private prepareModal(): void {
		if (!this.modalContent.value) {
			return;
		}

		this.modal.value = new ComplexModal(this.modalContent.value, {
			cssClassName: 'ImportCampaignConfigurationModalWrapper',
			destroyOnClose: true,
			onClose: this.closeModal.bind(this)
		});

		window.ModalManager.open(this.modal.value);
	}

	private async triggerLoadingAnimation(): Promise<void> {
		await nextTick();

		if (!this.importButton.value) {
			return;
		}

		Ladda.create(this.importButton.value).start();
	}
}

export { GENERIC_INELIGIBLE_MESSAGE };
